import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Handshake, Loader2, Package } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import { devError } from '../utils/logger';
import ListingCard from '../components/ListingCard';
import { Button } from '../components/ui/button';

export default function TradeExchangePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/b2b/listings?limit=200`);
        if (!cancelled) setListings(data || []);
      } catch (e) {
        if (cancelled) return;
        devError('b2b listings failed', e);
        toast.error('Could not load Trade Exchange');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Optional: hide own listings via toggle in future — for now show all so
  // suppliers can see what's been published in the network.
  const others = listings.filter((l) => l.supplier_id !== user?.user_id);
  const mine = listings.filter((l) => l.supplier_id === user?.user_id);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="b2b-loading">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="b2b-exchange-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Hero */}
        <div className="rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-transparent p-6 md:p-8 mb-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                <Handshake className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-1">Suppliers only</p>
                <h1 className="font-playfair text-3xl md:text-4xl font-bold text-foreground mb-2">
                  Trade Exchange
                </h1>
                <p className="text-sm text-muted-foreground max-w-2xl">
                  Surplus stock, giveaways, and specialised items between salvage yards.
                  Lean on each other's specialties — passing items along helps the whole network thrive.
                </p>
              </div>
            </div>
            <Link to="/listings/create">
              <Button className="btn-gold whitespace-nowrap" data-testid="b2b-list-item-btn">
                + List item to exchange
              </Button>
            </Link>
          </div>
        </div>

        {/* Empty state */}
        {others.length === 0 && mine.length === 0 && (
          <div className="text-center py-16 rounded-xl border border-border/20 bg-card/30" data-testid="b2b-empty">
            <Package className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground mb-2">No items in the Trade Exchange yet.</p>
            <p className="text-xs text-muted-foreground/60">
              When you create a listing, tick "Supplier-only" to add it here.
            </p>
          </div>
        )}

        {/* Other yards' offerings */}
        {others.length > 0 && (
          <section className="mb-12" data-testid="b2b-others-section">
            <h2 className="font-playfair text-xl text-foreground mb-4">
              From other yards <span className="text-muted-foreground/60 text-sm">· {others.length}</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="b2b-others-grid">
              {others.map((listing) => (
                <ListingCard key={listing.listing_id} listing={listing} />
              ))}
            </div>
          </section>
        )}

        {/* Own offerings */}
        {mine.length > 0 && (
          <section data-testid="b2b-mine-section">
            <h2 className="font-playfair text-xl text-foreground mb-4">
              Your trade-only listings <span className="text-muted-foreground/60 text-sm">· {mine.length}</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {mine.map((listing) => (
                <ListingCard key={listing.listing_id} listing={listing} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
